/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { LicenseTopCardFragment } from "@cosmos/hygraph-schema";

import {
	ButtonList,
	CheckListItems,
	CosmosButtonProps,
	CosmosCard,
	CosmosRichText,
	CosmosText,
	CosmosTextVariantEnum,
} from "@components/elements";
import { css, cx } from "@panda/css";
import { Stack } from "@panda/jsx";

import { styles } from "./LicenseTopCard.molecule.styles";

export type LicenseTopCardProps = LicenseTopCardFragment & {
	className?: string;
	titleAs?: "h1" | "h2" | "h3" | "h4";
};

export const LicenseTopCard = ({
	className = "",
	title,
	titleAs = "h3",
	bullets = [],
	checkList = [],
	buttons = [],
}: LicenseTopCardProps): React.JSX.Element | null => {
	if (!title && !bullets?.length && !checkList?.length && !buttons?.length) return null;

	const cosmosButtons = buttons.map((button): CosmosButtonProps => {
		return {
			...button,
			size: "lg",
			analytics: {
				eventCategory: "button",
				interactionType: "click",
				eventAction: "navigation",
				eventLabel: button.name || button.label || "",
				eventValue: {
					component: "LicenseCardCollapsed",
				},
			},
		};
	});

	return (
		<CosmosCard className={cx("license-collapsed-card", className, styles)} padding={32} variant="material">
			<Stack className="main-card-stack" gap="eds.dimension.32" direction="row">
				<Stack gap="eds.dimension.16" width="100%">
					{title ? (
						<CosmosText variant={CosmosTextVariantEnum.headingLG} text={title} as={titleAs} color="eds.text.primary" />
					) : null}
					{bullets?.length && bullets?.length > 1 ? (
						<ul className="bullets">
							{bullets?.map((bullet, i) => (
								<li key={`bullet-${i}`}>
									<CosmosRichText
										variant={CosmosTextVariantEnum.paragraphMD}
										color="eds.text.secondary"
										html={bullet?.html}
										className={css({
											"& p": {
												display: "inline",
											},
										})}
									/>
								</li>
							))}
						</ul>
					) : (
						<CosmosRichText html={bullets[0]?.html} color="eds.text.primary" variant="paragraphMD" />
					)}
				</Stack>
				<Stack gap="eds.dimension.24" width="100%">
					<ul
						className={cx(
							"checklist",
							css({
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-start",
							}),
						)}
					>
						<CheckListItems items={checkList || []} />
					</ul>
					<ButtonList buttons={cosmosButtons} />
				</Stack>
			</Stack>
		</CosmosCard>
	);
};
